import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { t, useLang } from "../lib/i18n";

export default function ContactPage() {
  useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAuthed = !loading && user && typeof user === "object";

  const onSubmit = async (e) => {
    e.preventDefault();
    const body = message.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/messages/contact", { body });
      toast.success("Message sent to the Reads team");
      setMessage("");
      navigate(`/messages/${data.thread_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-16 fade-up" data-testid="contact-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">{t("contact.eyebrow")}</div>
      <h1 className="font-heading text-5xl text-[#0A0A0A] mt-3 mb-4 tracking-tight">{t("contact.title")}</h1>
      <p className="text-[#4A4030] mb-10 leading-relaxed">
        {t("contact.subtitle")}
      </p>

      {isAuthed ? (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
          <label className="block space-y-1.5">
            <span className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030]">{t("contact.message")}</span>
            <textarea
              rows={8}
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("contact.whats_on_mind")}
              className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              data-testid="contact-message"
            />
          </label>
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={submitting || !message.trim()}
              className="bg-[#0A0A0A] hover:bg-[#1F1F1F] disabled:opacity-60 text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-8 py-3.5 rounded-sm transition-colors"
              data-testid="contact-submit"
            >
              {submitting ? t("contact.sending") : t("contact.send")}
            </button>
            <Link
              to="/messages"
              className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] hover:text-[#0A0A0A]"
              data-testid="contact-view-inbox"
            >
              {t("contact.view_inbox")} →
            </Link>
          </div>
        </form>
      ) : (
        <div className="border border-[rgba(10,10,10,0.10)] bg-[#FAF5E6] p-8" data-testid="contact-auth-cta">
          <div className="font-heading text-2xl text-[#0A0A0A] mb-3 tracking-tight">Sign in to send a message</div>
          <p className="text-[#4A4030] mb-6 text-sm leading-relaxed">
            Reads keeps all conversations inside the app so you can read replies, follow up, and keep
            the thread tidy. Create a free account or log in to start a thread with the team.
          </p>
          <div className="flex items-center gap-3">
            <Link
              to="/register"
              className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors"
              data-testid="contact-register"
            >
              Create account
            </Link>
            <Link
              to="/login"
              className="border border-[rgba(10,10,10,0.18)] bg-transparent hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors"
              data-testid="contact-login"
            >
              Log in
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
